'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { CheckCircle, CircleAlert, FileCheck, Send } from 'lucide-react';

interface ContractsTableWidgetProps {
  autoPlay?: boolean;
  className?: string;
}

type SignerStatus = 'pending' | 'completed';
type ContractStatus = 'sent' | 'signed';
type ContractType = 'reservation' | 'financing';

interface MockSigner {
  type: 'Inversor' | 'Promotor';
  status: SignerStatus;
}

interface MockContract {
  id: string;
  reference: string;
  type: ContractType;
  status: ContractStatus;
  signers: MockSigner[];
}

// Contract type badge colors matching the app
const contractTypeBadgeConfig: Record<ContractType, { bg: string; text: string; label: string }> = {
  reservation: {
    bg: 'rgba(59, 130, 246, 0.1)',
    text: '#3b82f6',
    label: 'Reserva',
  },
  financing: {
    bg: 'rgba(34, 197, 94, 0.1)',
    text: '#16a34a',
    label: 'Financiación',
  },
};

// Contract status badge colors matching badges.tsx exactly
const contractStatusConfig: Record<
  ContractStatus,
  { bg: string; text: string; iconColor: string }
> = {
  sent: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    iconColor: 'text-yellow-600',
  },
  signed: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    iconColor: 'text-green-600',
  },
};

// Signer status badge colors matching badges.tsx exactly
const signerStatusConfig: Record<SignerStatus, { bg: string; text: string; iconColor: string }> = {
  pending: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    iconColor: 'text-yellow-600',
  },
  completed: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    iconColor: 'text-green-600',
  },
};

// Initial mock contracts - 3 rows only
const initialContracts: MockContract[] = [
  {
    id: 'DOC-A1B2',
    reference: '#INV-7F3E',
    type: 'reservation',
    status: 'signed',
    signers: [
      { type: 'Inversor', status: 'completed' },
      { type: 'Promotor', status: 'completed' },
    ],
  },
  {
    id: 'DOC-C3D4', // This is the emphasized row - where animation happens
    reference: '#INV-2K9M',
    type: 'financing',
    status: 'sent',
    signers: [
      { type: 'Inversor', status: 'pending' },
      { type: 'Promotor', status: 'pending' },
    ],
  },
  {
    id: 'DOC-E5F6',
    reference: '#INV-5P1Q',
    type: 'financing',
    status: 'sent',
    signers: [
      { type: 'Inversor', status: 'pending' },
      { type: 'Promotor', status: 'pending' },
    ],
  },
];

// The row that will animate
const ANIMATED_CONTRACT_ID = 'DOC-C3D4';

export function ContractsTableWidget({ autoPlay = true, className }: ContractsTableWidgetProps) {
  const [contracts, setContracts] = useState<MockContract[]>(initialContracts);
  const animationRef = useRef<boolean>(true);

  useEffect(() => {
    if (!autoPlay) return;
    animationRef.current = true;

    const runSequence = async () => {
      // 1. Reset to initial state
      setContracts(initialContracts);

      // 2. Pause to let the table be seen
      await delay(2500);
      if (!animationRef.current) return;

      // 3. First signer signs (Inversor)
      setContracts((prev) =>
        prev.map((contract) =>
          contract.id === ANIMATED_CONTRACT_ID
            ? {
                ...contract,
                signers: contract.signers.map((signer, idx) =>
                  idx === 0 ? { ...signer, status: 'completed' as SignerStatus } : signer
                ),
              }
            : contract
        )
      );

      // 4. Pause between signers
      await delay(1500);
      if (!animationRef.current) return;

      // 5. Second signer signs (Promotor)
      setContracts((prev) =>
        prev.map((contract) =>
          contract.id === ANIMATED_CONTRACT_ID
            ? {
                ...contract,
                signers: contract.signers.map((signer) => ({
                  ...signer,
                  status: 'completed' as SignerStatus,
                })),
              }
            : contract
        )
      );

      // 6. Short pause before contract status updates
      await delay(800);
      if (!animationRef.current) return;

      // 7. Contract status updates to signed
      setContracts((prev) =>
        prev.map((contract) =>
          contract.id === ANIMATED_CONTRACT_ID
            ? { ...contract, status: 'signed' as ContractStatus }
            : contract
        )
      );

      // 8. Pause to appreciate the completed state
      await delay(3000);
      if (!animationRef.current) return;

      // 9. Loop
      if (animationRef.current) runSequence();
    };

    runSequence();
    return () => {
      animationRef.current = false;
    };
  }, [autoPlay]);

  const renderContractTypeBadge = (type: ContractType, isLarge = false) => {
    const config = contractTypeBadgeConfig[type];
    return (
      <span
        className={`inline-flex items-center rounded font-medium whitespace-nowrap ${isLarge ? 'px-2 py-1 text-xs' : 'px-1.5 py-0.5 text-[10px]'}`}
        style={{ backgroundColor: config.bg, color: config.text }}
      >
        {config.label}
      </span>
    );
  };

  const renderContractStatusBadge = (
    status: ContractStatus,
    isAnimated: boolean,
    isLarge = false
  ) => {
    const config = contractStatusConfig[status];
    const Icon = status === 'signed' ? FileCheck : Send;

    return (
      <AnimatePresence mode="wait">
        <motion.span
          key={status}
          initial={isAnimated ? { scale: 0.8, opacity: 0 } : false}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={`inline-flex items-center rounded font-medium whitespace-nowrap ${config.bg} ${config.text} ${isLarge ? 'px-2 py-1 text-xs' : 'px-1.5 py-0.5 text-[10px]'}`}
        >
          <Icon
            className={`${isLarge ? 'w-3.5 h-3.5 mr-1.5' : 'w-3 h-3 mr-1'} ${config.iconColor}`}
          />
          {status === 'signed' ? 'Firmado' : 'Enviado'}
        </motion.span>
      </AnimatePresence>
    );
  };

  const renderSignerBadge = (
    signer: MockSigner,
    isAnimated: boolean,
    index: number,
    isLarge = false
  ) => {
    const config = signerStatusConfig[signer.status];
    const Icon = signer.status === 'completed' ? CheckCircle : CircleAlert;

    return (
      <AnimatePresence mode="wait">
        <motion.span
          key={`${signer.type}-${signer.status}`}
          initial={isAnimated ? { scale: 0.8, opacity: 0 } : false}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ duration: 0.25, delay: isAnimated ? index * 0.05 : 0 }}
          className={`inline-flex items-center rounded font-medium whitespace-nowrap ${config.bg} ${config.text} ${isLarge ? 'px-2 py-1 text-xs' : 'px-1.5 py-0.5 text-[10px]'}`}
        >
          <Icon
            className={`${isLarge ? 'w-3 h-3 mr-1' : 'w-2.5 h-2.5 mr-0.5'} ${config.iconColor}`}
          />
          {signer.type}
        </motion.span>
      </AnimatePresence>
    );
  };

  const renderRow = (contract: MockContract, index: number) => {
    const isEmphasized = contract.id === ANIMATED_CONTRACT_ID;
    const isAnimated = isEmphasized;

    if (isEmphasized) {
      // Emphasized row pops out of the table
      return (
        <motion.div
          key={contract.id}
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.3, delay: index * 0.08 }}
          className="mx-[-12px] px-5 py-3 bg-white rounded-lg border border-[#d4d4d8] grid grid-cols-4 items-center gap-2"
          style={{
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)',
          }}
        >
          <span className="font-semibold text-[#0a0a0a] font-mono text-sm">
            {contract.reference}
          </span>
          <div className="flex justify-center">{renderContractTypeBadge(contract.type, true)}</div>
          <div className="flex justify-center">
            {renderContractStatusBadge(contract.status, isAnimated, true)}
          </div>
          <div className="flex flex-col items-center gap-1">
            {contract.signers.map((signer, idx) => (
              <span key={idx}>{renderSignerBadge(signer, isAnimated, idx, true)}</span>
            ))}
          </div>
        </motion.div>
      );
    }

    // Normal row
    return (
      <motion.div
        key={contract.id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.08 }}
        className="px-3 py-1.5 border-b border-[#e4e4e7] last:border-b-0 grid grid-cols-4 items-center gap-2"
      >
        <span className="font-medium text-[#0a0a0a] font-mono text-[11px]">
          {contract.reference}
        </span>
        <div className="flex justify-center">{renderContractTypeBadge(contract.type)}</div>
        <div className="flex justify-center">
          {renderContractStatusBadge(contract.status, false)}
        </div>
        <div className="flex flex-col items-center gap-0.5">
          {contract.signers.map((signer, idx) => (
            <span key={idx}>{renderSignerBadge(signer, false, idx)}</span>
          ))}
        </div>
      </motion.div>
    );
  };

  return (
    <div className={className}>
      <div
        className="relative mx-auto"
        style={{
          maxWidth: '480px',
        }}
      >
        {/* Table container */}
        <div
          className="bg-white border border-[#e4e4e7] rounded-lg overflow-visible"
          style={{
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)',
          }}
        >
          {/* Table header */}
          <div className="px-3 py-1.5 bg-[#fafaf9] border-b border-[#e4e4e7] rounded-t-lg grid grid-cols-4 gap-2">
            <span className="text-[10px] font-medium text-[#71717a] uppercase tracking-wider">
              Referencia
            </span>
            <span className="text-[10px] font-medium text-[#71717a] uppercase tracking-wider text-center">
              Tipo
            </span>
            <span className="text-[10px] font-medium text-[#71717a] uppercase tracking-wider text-center">
              Estado
            </span>
            <span className="text-[10px] font-medium text-[#71717a] uppercase tracking-wider text-center">
              Firmantes
            </span>
          </div>

          {/* Table body */}
          <div className="bg-white rounded-b-lg">
            {contracts.map((contract, index) => renderRow(contract, index))}
          </div>
        </div>

        {/* Stacked cards effect below */}
        <div className="mx-3 h-1.5 bg-[#f5f5f5] border-x border-b border-[#e4e4e7] rounded-b" />
        <div className="mx-5 h-1.5 bg-[#ebebeb] border-x border-b border-[#e4e4e7] rounded-b" />
      </div>
    </div>
  );
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default ContractsTableWidget;
